import { DecimalPipe, NgClass } from '@angular/common';
import {
  Component,
  computed,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal
} from '@angular/core';
import { ButtonComponent } from '../button/button.component';
import { WooCommerceProduct } from '../../../core/interfaces/woo-commerce/api/products/product-api.interface';
import { ButtonSize, ButtonStyle } from '../../enums/button.enum';
import { FormattedCurrencyPipe } from '../../pipes/formatted-currency.pipe';
import { replacePreTags } from '../../utils/text.utils';

@Component({
  selector: 'app-meal-card',
  imports: [ButtonComponent, DecimalPipe, NgClass, FormattedCurrencyPipe],
  templateUrl: './meal-card.component.html',
  styleUrl: './meal-card.component.scss'
})
export class MealCardComponent {
  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonStyle = ButtonStyle;

  meal: InputSignal<WooCommerceProduct> = input.required<WooCommerceProduct>();
  onAddToCart: OutputEmitterRef<void> = output<void>();

  /** First in-stock variant, or first variant if all are out of stock. */
  private firstVariant = computed(() => {
    const variants = this.meal().variants || [];
    return variants.find((v) => v.is_in_stock) ?? variants[0] ?? null;
  });

  processedDescription: Signal<string> = computed(() => {
    const meal = this.meal();
    const variant = this.firstVariant();
    const description =
      variant?.description || variant?.short_description || meal?.description || '';
    return replacePreTags(description);
  });

  /**
   * Resolved display image URL.
   * Prefers the parent product image; falls back to the first variant image
   * when the parent has none (common for products that are variant containers).
   */
  displayImage: Signal<string | undefined> = computed(() => {
    const meal = this.meal();
    const parentImage = meal.images?.[0]?.src || meal.clover_pos_images?.[0];
    if (parentImage) {
      return parentImage;
    }
    return this.firstVariant()?.main_image?.url ?? undefined;
  });

  /**
   * True when the parent product has no meaningful price of its own and should
   * display the first variant's price instead.
   */
  isVariantProduct: Signal<boolean> = computed(() => {
    const meal = this.meal();
    return (
      meal.has_variants &&
      (meal.variants?.length ?? 0) > 0 &&
      parseFloat(meal.prices.regular_price) === 0
    );
  });

  /**
   * Show "From" when not all available variant prices are the same.
   */
  showFromPrefix: Signal<boolean> = computed(() => {
    if (!this.isVariantProduct()) return false;
    const prices = (this.meal().variants || [])
      .filter((v) => v.is_in_stock)
      .map((v) => v.effective_price ?? v.regular_price ?? 0);
    if (prices.length < 2) return false;
    return prices.some((p) => p !== prices[0]);
  });

  /**
   * The price (in minor units) to display on the card.
   * Falls back to the parent product price for non-variant products.
   */
  displayRegularPrice: Signal<string> = computed(() => {
    if (this.isVariantProduct()) {
      const variant = this.firstVariant();
      if (variant) {
        const minorUnit = this.meal().prices.currency_minor_unit ?? 2;
        return String(Math.round((variant.regular_price ?? 0) * 10 ** minorUnit));
      }
    }
    return this.meal().prices.regular_price;
  });

  displayEffectivePrice: Signal<string> = computed(() => {
    if (this.isVariantProduct()) {
      const variant = this.firstVariant();
      if (variant) {
        const minorUnit = this.meal().prices.currency_minor_unit ?? 2;
        return String(Math.round((variant.effective_price ?? variant.regular_price ?? 0) * 10 ** minorUnit));
      }
    }
    return this.meal().prices.price;
  });

  discountPercentage: Signal<number> = computed<number>(() => {
    const regular = parseFloat(this.displayRegularPrice());
    const effective = parseFloat(this.displayEffectivePrice());

    if (!isNaN(regular) && !isNaN(effective) && regular > 0 && effective < regular) {
      return ((regular - effective) / regular) * 100;
    }

    return 0;
  });
}
